import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authApi, babyApi, eventApi } from '../services/api';
import { useNotifications } from '../hooks/useNotifications';
import {
  generateRSAKeyPair,
  exportPublicKey,
  encryptPrivateKey,
  getBabyKey,
  decryptBabyProfile,
  decryptEventData,
  sessionKeys,
  reEncryptBabyKeys,
} from '../crypto';

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isSupported, isSubscribed, subscribe, loading: notifLoading, sendTestNotification } = useNotifications();

  // Name change state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);

  // Modal states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showExportData, setShowExportData] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 2FA state
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQR, setTwoFactorQR] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [isSettingUp2FA, setIsSettingUp2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState('');

  // Delete account state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Password validation
  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[^A-Za-z0-9]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  // Handle name change
  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed) {
      setNameError('Name cannot be empty');
      return;
    }
    if (trimmed === user?.name) {
      setIsEditingName(false);
      return;
    }

    setIsSavingName(true);
    setNameError(null);
    try {
      await authApi.updateProfile({ name: trimmed });
      // Update local auth state
      useAuth.setState((state) => ({
        user: state.user ? { ...state.user, name: trimmed } : null,
      }));
      setIsEditingName(false);
    } catch (err) {
      setNameError((err as Error).message || 'Failed to update name');
    } finally {
      setIsSavingName(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    setPasswordError(null);

    // Validate new password
    const pwdErrors = validatePassword(newPassword);
    if (pwdErrors.length > 0) {
      setPasswordError(`Password must have: ${pwdErrors.join(', ')}`);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      // Generate new RSA key pair (password change requires new keys)
      const keyPair = await generateRSAKeyPair();
      const publicKey = await exportPublicKey(keyPair.publicKey);
      const encryptedPrivateKey = await encryptPrivateKey(keyPair.privateKey, newPassword);

      // Re-encrypt all baby keys with the new public key
      const currentBabyKeys = sessionKeys.getAllBabyKeys();
      let reEncryptedBabyKeysObj: Record<string, string> | undefined;
      if (currentBabyKeys.size > 0) {
        const reEncryptedMap = await reEncryptBabyKeys(currentBabyKeys, publicKey);
        reEncryptedBabyKeysObj = Object.fromEntries(reEncryptedMap);
      }

      await authApi.changePassword({
        currentPassword,
        newPassword,
        publicKey,
        encryptedPrivateKey,
        reEncryptedBabyKeys: reEncryptedBabyKeysObj,
      });

      alert('Password changed successfully! Please log in again.');
      await logout();
      navigate('/login');
    } catch (err) {
      setPasswordError((err as Error).message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Handle 2FA setup
  const handleSetup2FA = async () => {
    setIsSettingUp2FA(true);
    setTwoFactorError(null);

    try {
      const response = await authApi.setup2FA();
      setTwoFactorSecret(response.data!.secret);
      setTwoFactorQR(response.data!.qrCodeUrl);
    } catch (err) {
      setTwoFactorError((err as Error).message || 'Failed to setup 2FA');
    } finally {
      setIsSettingUp2FA(false);
    }
  };

  // Verify and enable 2FA
  const handleVerify2FA = async () => {
    if (twoFactorCode.length !== 6) {
      setTwoFactorError('Please enter a 6-digit code');
      return;
    }

    setIsSettingUp2FA(true);
    setTwoFactorError(null);

    try {
      await authApi.verify2FA(twoFactorCode);
      alert('Two-factor authentication enabled!');
      setShow2FASetup(false);
      setTwoFactorCode('');
      setTwoFactorSecret('');
      setTwoFactorQR('');
      // Refresh page to update user state
      window.location.reload();
    } catch (err) {
      setTwoFactorError((err as Error).message || 'Invalid code');
    } finally {
      setIsSettingUp2FA(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setTwoFactorError('Please enter your password');
      return;
    }

    setIsSettingUp2FA(true);
    setTwoFactorError(null);

    try {
      await authApi.disable2FA(disablePassword);
      alert('Two-factor authentication disabled!');
      setShow2FASetup(false);
      setDisablePassword('');
      window.location.reload();
    } catch (err) {
      setTwoFactorError((err as Error).message || 'Failed to disable 2FA');
    } finally {
      setIsSettingUp2FA(false);
    }
  };

  // Handle data export
  const handleExportData = async () => {
    setIsExporting(true);
    setExportProgress('Fetching your data...');

    try {
      // Fetch all babies
      const babiesResponse = await babyApi.list();
      const babies = babiesResponse.data || [];

      const exportData: any = {
        exportedAt: new Date().toISOString(),
        user: {
          name: user?.name,
          email: user?.email,
          createdAt: user?.createdAt,
        },
        babies: [],
      };

      // Process each baby
      for (const baby of babies) {
        setExportProgress(`Decrypting ${baby.id}...`);

        try {
          const aesKey = await getBabyKey(baby.id);
          if (!aesKey) {
            continue;
          }

          const profile = await decryptBabyProfile(baby.encryptedData, aesKey);

          // Fetch events for this baby
          setExportProgress(`Fetching events for ${profile.name}...`);
          const eventsResponse = await eventApi.list(baby.id, { limit: 10000 });
          const events = eventsResponse.data?.events || [];

          // Decrypt events
          const decryptedEvents = [];
          for (const event of events) {
            try {
              const eventData = await decryptEventData(event.encryptedData, aesKey);
              decryptedEvents.push({
                type: event.type,
                timestamp: event.timestamp,
                data: eventData,
              });
            } catch (eventErr) {
              console.error('Export: Failed to decrypt event', event.id, eventErr);
            }
          }

          exportData.babies.push({
            profile,
            events: decryptedEvents,
            role: baby.role,
          });
        } catch (babyErr) {
          console.error('Export: Failed to process baby', baby.id, babyErr);
        }
      }

      // Create and download JSON file
      setExportProgress('Creating export file...');
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mytottot-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowExportData(false);
      alert('Data exported successfully!');
    } catch (err) {
      alert('Failed to export data: ' + (err as Error).message);
    } finally {
      setIsExporting(false);
      setExportProgress('');
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setDeleteError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!deletePassword) {
      setDeleteError('Please enter your password');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await authApi.deleteAccount(deletePassword);
      alert('Your account has been deleted. You will now be logged out.');
      await logout();
      navigate('/');
    } catch (err) {
      setDeleteError((err as Error).message || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Settings
      </h1>

      <div className="space-y-6">
        {/* Profile section */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Profile
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400">Name</label>
              {isEditingName ? (
                <div className="mt-1 space-y-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="input"
                    placeholder="Your name"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') {
                        setIsEditingName(false);
                        setEditName(user?.name || '');
                        setNameError(null);
                      }
                    }}
                  />
                  {nameError && (
                    <p className="text-sm text-red-500">{nameError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveName}
                      disabled={isSavingName}
                      className="btn-primary btn-sm"
                    >
                      {isSavingName ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false);
                        setEditName(user?.name || '');
                        setNameError(null);
                      }}
                      className="btn-secondary btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-gray-900 dark:text-white">{user?.name}</p>
                  <button
                    onClick={() => {
                      setEditName(user?.name || '');
                      setIsEditingName(true);
                    }}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-500 dark:text-gray-400">Email</label>
              <p className="text-gray-900 dark:text-white">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Notifications section */}
        {isSupported && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Notifications
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-900 dark:text-white">Push Notifications</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {isSubscribed ? 'Enabled on this device' : 'Enable to receive reminders'}
                  </p>
                </div>
                {!isSubscribed ? (
                  <button
                    onClick={subscribe}
                    disabled={notifLoading}
                    className="btn-primary btn-sm"
                  >
                    {notifLoading ? 'Enabling...' : 'Enable'}
                  </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={sendTestNotification} className="btn-secondary btn-sm">
                            Test
                        </button>
                        <span className="text-green-600 dark:text-green-400 text-sm flex items-center">
                            ✓ Active
                        </span>
                    </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security section */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Security
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-900 dark:text-white">Two-Factor Authentication</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <button
                onClick={() => setShow2FASetup(true)}
                className="btn-secondary btn-sm"
              >
                {user?.twoFactorEnabled ? 'Disable' : 'Enable'}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-900 dark:text-white">Change Password</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Update your password
                </p>
              </div>
              <button
                onClick={() => setShowChangePassword(true)}
                className="btn-secondary btn-sm"
              >
                Change
              </button>
            </div>
          </div>
        </div>

        {/* Privacy section */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Privacy & Data
          </h2>
          <div className="space-y-4">
            <Link
              to="/app/export"
              className="btn-secondary w-full justify-start flex items-center gap-2"
            >
              <span>📊</span>
              <span>Export Reports (PDF/CSV)</span>
            </Link>
            <button
              onClick={() => setShowExportData(true)}
              className="btn-secondary w-full justify-start flex items-center gap-2"
            >
              <span>💾</span>
              <span>Export Raw Data (JSON)</span>
            </button>
            <button
              onClick={() => setShowDeleteAccount(true)}
              className="btn-danger w-full justify-start"
            >
              Delete Account
            </button>
          </div>
        </div>

        {/* Support section */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Support myTotTot
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            myTotTot is free to use, forever. If you find it helpful and are able to,
            consider buying us a coffee to help keep the project running.
          </p>
          <a
            href="https://buymeacoffee.com/mearkats"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary w-full justify-center inline-flex items-center gap-2"
          >
            <span>Buy us a coffee</span>
            <span>☕</span>
          </a>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Change Password
            </h3>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {passwordError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder="Enter new password"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Must be at least 12 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="input"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="btn-primary flex-1"
              >
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </button>
              <button
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                  setPasswordError(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {user?.twoFactorEnabled ? 'Disable' : 'Enable'} Two-Factor Authentication
            </h3>

            {twoFactorError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {twoFactorError}
              </div>
            )}

            {user?.twoFactorEnabled ? (
              // Disable 2FA
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enter your password to disable two-factor authentication.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    className="input"
                    placeholder="Enter your password"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleDisable2FA}
                    disabled={isSettingUp2FA}
                    className="btn-danger flex-1"
                  >
                    {isSettingUp2FA ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                  <button
                    onClick={() => {
                      setShow2FASetup(false);
                      setDisablePassword('');
                      setTwoFactorError(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : twoFactorQR ? (
              // Verify 2FA code
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Scan this QR code with your authenticator app, then enter the 6-digit code.
                </p>
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={twoFactorQR} alt="2FA QR Code" className="w-48 h-48" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Manual Entry Code
                  </label>
                  <code className="block p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm break-all">
                    {twoFactorSecret}
                  </code>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleVerify2FA}
                    disabled={isSettingUp2FA || twoFactorCode.length !== 6}
                    className="btn-primary flex-1"
                  >
                    {isSettingUp2FA ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                  <button
                    onClick={() => {
                      setShow2FASetup(false);
                      setTwoFactorCode('');
                      setTwoFactorSecret('');
                      setTwoFactorQR('');
                      setTwoFactorError(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Start 2FA setup
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Two-factor authentication adds an extra layer of security to your account.
                  You'll need an authenticator app like Google Authenticator or Authy.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleSetup2FA}
                    disabled={isSettingUp2FA}
                    className="btn-primary flex-1"
                  >
                    {isSettingUp2FA ? 'Setting up...' : 'Set Up 2FA'}
                  </button>
                  <button
                    onClick={() => setShow2FASetup(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Data Modal */}
      {showExportData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Export Your Data
            </h3>

            {isExporting ? (
              <div className="text-center py-8">
                <div className="spinner mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">{exportProgress}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  This will download all your data including baby profiles and logged events
                  as a JSON file. Your data will be decrypted locally before export.
                </p>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg mb-4">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    ⚠️ The exported file will contain unencrypted data. Keep it secure!
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleExportData}
                    className="btn-primary flex-1"
                  >
                    Export Data
                  </button>
                  <button
                    onClick={() => setShowExportData(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
              Delete Account
            </h3>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {deleteError}
              </div>
            )}

            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-4">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                ⚠️ This action cannot be undone!
              </p>
              <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                <li>All your baby profiles will be deleted</li>
                <li>All logged events and photos will be lost</li>
                <li>Shared access for caregivers will be revoked</li>
                <li>Your encryption keys will be destroyed</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type "DELETE MY ACCOUNT" to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="input"
                  placeholder="DELETE MY ACCOUNT"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Enter your password
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="input"
                  placeholder="Your password"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT'}
                className="btn-danger flex-1"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account Forever'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteAccount(false);
                  setDeletePassword('');
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
